import { useState, useEffect, useMemo, useRef } from "react";
import "./Customerworkshopview.css";
import bgImage from "../../../Assets/WorkshopForm/Workshop_Dashboard1.png";
import CustomerWorkshopView from "./Customerworkshopview";
import { getCaseById } from "./workshopApi";
import { useParams, useNavigate } from 'react-router-dom';
// ─── React Query hooks ────────────────────────────────────────
import {
  useAllCases,
  useSearchCase,
  useGlobalCaseSearch,
  useSubmitCase,
  useUploadDocuments, 
} from "./workshopHooks";

// ─── Document field keys (for building file upload list) ──────
import {
  DOCUMENT_FIELDS,
  DRIVER_DOC_FIELDS,
  HELPER_DOC_FIELDS,
  FIR_DOC_FIELD,
  LR_DOC_FIELD,
  ACCIDENT_IMAGE_FIELDS,
} from "./workshopConstants";
import axios from "axios";

// ═══════════════════════════════════════════════════════════════
// THEME
// ═══════════════════════════════════════════════════════════════
const getIsDark = () => {
  try { return localStorage.getItem("cw-theme") === "dark"; } catch { return false; }
};
const applyTheme = (isDark) => {
  try { localStorage.setItem("cw-theme", isDark ? "dark" : "light"); } catch {}
  document.body.classList.toggle("theme-dark",  isDark);
  document.body.classList.toggle("theme-light", !isDark);
};

// ═══════════════════════════════════════════════════════════════
// SHARED UI PRIMITIVES
// ═══════════════════════════════════════════════════════════════
const Toggle = ({ isDark, onToggle }) => (
  <button className="cw-toggle" onClick={onToggle}
    title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}>
    <div className="cw-toggle-track">
      <div className="cw-toggle-knob">{isDark ? "🌙" : "☀️"}</div>
    </div>
    <span className="cw-toggle-label">{isDark ? "DARK" : "LIGHT"}</span>
  </button>
);

const Bg = () => (
  <>
    <div className="cw-bg-img" style={{ backgroundImage: `url(${bgImage})` }} />
    <div className="cw-bg-overlay" />
  </>
);

const Nav = ({ left, center, right }) => (
  <nav className="cw-nav">
    {left}
    {center && <div className="cw-nav-cid">{center}</div>}
    {right}
  </nav>
);


const Btn = ({ children, onClick, cls = "cw-btn-primary", sx, disabled }) => (
  <button className={`cw-btn ${cls}`} onClick={onClick} disabled={disabled}
    style={{ ...sx, opacity: disabled ? 0.55 : 1, cursor: disabled ? "not-allowed" : "pointer" }}>
    {children}
  </button>
);

const Badge = ({ id }) => <span className="cw-badge cw-mono">{id}</span>;

const Field = ({ label, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    <label className="cw-label">{label}</label>
    {children}
  </div>
);

const getInp = (isDark) => ({
  padding: "10px 13px", borderRadius: 9, fontSize: 14, width: "100%",
  fontFamily: "'DM Sans', sans-serif",
  background: isDark ? "rgba(30,27,75,0.8)" : "#fff",
  color: isDark ? "#f1f5f9" : "#1e1b4b",
  border: isDark ? "1px solid rgba(125,211,252,0.2)" : "1.5px solid rgba(99,102,241,0.18)",
  boxShadow: isDark ? "none" : "0 2px 8px rgba(99,102,241,0.06), inset 0 1px 0 rgba(255,255,255,0.8)",
  transition: "border-color 0.2s, box-shadow 0.2s",
});

const Section = ({ children }) => (
  <div style={{ marginTop: 26, marginBottom: 8 }}>
    <p className="cw-section-label cw-syne">{children}</p>
  </div>
);

const FileUpload = ({ onChange, isDark, accept }) => {
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const inputRef        = useRef(null);
 
  const fmtSize = (b) =>
    b < 1024 ? `${b} B`
    : b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB`
    : `${(b / 1024 / 1024).toFixed(1)} MB`;
 
  // Native input change → pass real event straight to handleFile
  const handleChange = (e) => {
    setFile(e.target.files[0] || null);
    onChange(e);                         // ← your original handleFile(key)(e)
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files[0];
    if (!f) return;
    const dt = new DataTransfer();
    dt.items.add(f);
    inputRef.current.files = dt.files;       // write into real input
    setFile(f);
    onChange({ target: inputRef.current });  // fire with real input as target
  };
  const clear = (e) => {
    e.stopPropagation();
    inputRef.current.value = "";
    setFile(null);
    onChange({ target: inputRef.current }); // files[0] = undefined → null stored
  };
  const filled = !!file;
 
  const zoneStyle = {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: 10,
    minHeight: 44,
    padding: "8px 12px",
    borderRadius: 9,
    cursor: "pointer",
    transition: "border-color 0.18s, background 0.15s",
    border: filled
      ? isDark ? "1.5px solid rgba(99,102,241,0.5)"     : "1.5px solid rgba(99,102,241,0.4)"
      : drag
        ? isDark ? "1.5px dashed rgba(125,211,252,0.5)" : "1.5px dashed rgba(99,102,241,0.4)"
        : isDark ? "1.5px dashed rgba(255,255,255,0.15)": "1.5px dashed rgba(99,102,241,0.22)",
    background: filled
      ? isDark ? "rgba(99,102,241,0.08)" : "rgba(99,102,241,0.05)"
      : drag
        ? isDark ? "rgba(125,211,252,0.06)" : "rgba(99,102,241,0.04)"
        : isDark ? "rgba(255,255,255,0.03)" : "rgba(99,102,241,0.02)",
    overflow: "hidden",
  };
  const iconStyle = {
    width: 28, height: 28, borderRadius: 6, flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14,
    background: filled
      ? isDark ? "rgba(99,102,241,0.2)"   : "rgba(99,102,241,0.12)"
      : isDark ? "rgba(255,255,255,0.06)" : "rgba(99,102,241,0.07)",
    border: filled
      ? isDark ? "1px solid rgba(99,102,241,0.3)" : "1px solid rgba(99,102,241,0.2)"
      : isDark ? "1px solid rgba(255,255,255,0.1)": "1px solid rgba(99,102,241,0.12)",
  };
 
  return (
    <div
      style={zoneStyle}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={handleDrop}
    >
      {/*
        Real <input type="file"> kept in the DOM at 1×1px / opacity 0.
        The browser's "no file chosen" text is never rendered because
        the input is invisible — but it is fully functional.
      */}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleChange}
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: "none",
        }}
      />
 
      {/* Icon */}
      <div style={iconStyle}>
        {filled ? "✓" : "↑"}
      </div>
 
      {/* Label + meta */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 12, fontWeight: 600, lineHeight: 1.4,
          color: filled
            ? isDark ? "#a5b4fc" : "#4f46e5"
            : isDark ? "rgba(255,255,255,0.65)" : "rgba(30,27,75,0.6)",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {filled ? file.name : "Choose file"}
        </div>
        {filled && (
          <div style={{
            fontSize: 11, marginTop: 1,
            color: isDark ? "rgba(165,180,252,0.7)" : "rgba(79,70,229,0.6)",
          }}>
            {fmtSize(file.size)}
          </div>
        )}
      </div>
 
      {/* Clear button — only shown when a file is selected */}
      {filled && (
        <button
          onClick={clear}
          style={{
            background: "none", border: "none", cursor: "pointer", flexShrink: 0,
            fontSize: 13, lineHeight: 1, padding: "4px 5px", borderRadius: 5,
            color: isDark ? "rgba(255,255,255,0.4)" : "rgba(30,27,75,0.35)",
          }}
          title="Remove file"
        >✕</button>
      )}
    </div>
  );
};

const INITIAL_FORM = {
  driverName: "",
  driverNumber: "",
  vehicleNumber: "",
  driverDlNumber: "",
  driverDob: "", 
  helperPresent: "No",
  helperName: "",
  helperNumber: "",
  helperDlNumber: "",
  helperDob: "",
  rootCause: "",
  accidentDate: "",
  isLoaded: "No",
  thirdPartyLoss: "No",
  firNumber: "",
  firOrGdDate: "",
  claimNumber: "",
  insuranceCompany: "",
  policyNumber: "",
  isAdvocateNeeded: "No",
  powerOfAttorneyHolderName: "",
  powerOfAttorneyHolderAadhar: "",
  vehicleReleaseDate: "",
  jamantiDate: "",
  companyRepresentativeName: "",
  companyRepresentativeEmail: "",
  companyRepresentativePhone: "",
};

const toDateInput = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

const toDateTimeInput = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 16);
};

const mapDetailsToForm = (details = {}) => ({
  ...INITIAL_FORM,
  driverName: details.driverName || "",
  driverNumber: details.driverNumber || "",
  vehicleNumber: details.vehicleNumber || "",      
  driverDlNumber: details.driverDlNumber || details.driverDLNumber || details.driverDlNo || details.driverDLNo || details.driver_dl_number || details.driver_DL_number || "",
  driverDob: toDateInput(details.driverDob) || "",
  helperPresent: details.helperPresent ? "Yes" : "No",
  helperName: details.helperName || "",
  helperNumber: details.helperNumber || "",
  helperDlNumber: details.helperDlNumber || details.helperDLNumber || details.helperDlNo || details.helperDLNo || details.helper_dl_number || details.helper_DL_number || "",    
  helperDob: toDateInput(details.helperDob) || "", 
  rootCause: details.rootCause || "",
  accidentDate: toDateTimeInput(details.accidentDate),
  isLoaded: details.isLoaded ? "Yes" : "No",
  thirdPartyLoss: details.thirdPartyLoss ? "Yes" : "No",
  firNumber: details.firNumber || "",
  firOrGdDate: toDateInput(details.firOrGdDate),
  claimNumber: details.claimNumber || "",
  insuranceCompany: details.insuranceCompany || "",
  policyNumber: details.policyNumber || "",
  isAdvocateNeeded: details.isAdvocateNeeded ? "Yes" : "No",
  powerOfAttorneyHolderName: details.powerOfAttorneyHolderName || "",
  powerOfAttorneyHolderAadhar: details.powerOfAttorneyHolderAadhar || "",
  vehicleReleaseDate: toDateInput(details.vehicleReleaseDate),
  jamantiDate: toDateInput(details.jamantiDate),
  companyRepresentativeName: details.companyRepresentativeName || "",
  companyRepresentativeEmail: details.companyRepresentativeEmail || "",
  companyRepresentativePhone: details.companyRepresentativePhone || "",
});

// Coloured inline alert banner
const Alert = ({ msg, type }) => {
  if (!msg) return null;
  const p = {
    error:   { bg: "rgba(239,68,68,0.08)",  border: "rgba(239,68,68,0.3)",  color: "#dc2626" },
    success: { bg: "rgba(16,185,129,0.08)", border: "rgba(16,185,129,0.3)", color: "#059669" },
    info:    { bg: "rgba(99,102,241,0.08)", border: "rgba(99,102,241,0.3)", color: "#4f46e5" },
  }[type] || {};
  return (
    <div style={{
      padding: "10px 14px", borderRadius: 10, marginTop: 12,
      fontSize: 13, fontWeight: 600, lineHeight: 1.5,
      background: p.bg, border: `1.5px solid ${p.border}`, color: p.color,
    }}>{msg}</div>
  );
};

// ═══════════════════════════════════════════════════════════════
// SCREEN 1 — CASE ENTRY
// Hooks: useAllCases (table), useSearchCase (search button)
// ═══════════════════════════════════════════════════════════════
const CaseEntry = ({ onContinue, onView, onEdit, isDark, onToggle }) => {
  const [query, setQuery] = useState("");
  const [hasUpdate, setHasUpdate] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [globalSearchResult, setGlobalSearchResult] = useState(null);
  const [globalSearching, setGlobalSearching] = useState(false);
  const [globalSearchError, setGlobalSearchError] = useState(null);
  const [prevCaseStatuses, setPrevCaseStatuses] = useState({});
  const [updatedCases, setUpdatedCases] = useState([]);

  // Important statuses that trigger notifications
  const IMPORTANT_STATUSES = [
    "DOCUMENTS_SUBMITTED",
    "PAYMENT_REQUEST_SENT",
    "PAYMENT_COMPLETED",
    "WORKSHOP_ESTIMATE",
    "WORKSHOP_SURVEY_COMPLETED",
    "VEHICLE_RECEIVED_AT_WORKSHOP",
    "ESTIMATE_APPROVED",
    "CLAIM_FORM_APPROVED",
    "CLAIM_SUBMITTED",
    "READY_FOR_INSPECTION",
    "VEHICLE_DELIVERED",
    "CLAIM_SETTLED",
  ];

  // ── useAllCases ───────────────────────────────────────────────
  // data = array of cases | isLoading = spinner | isError = error msg
  const {
    data:      allCases = [],
    isLoading: casesLoading,
    isError:   casesError,
    error:     casesErrorMsg,
    refetch:   refetchCases,
  } = useAllCases();

  useEffect(() => {
    // Check for status changes in cases
    const newUpdates = [];
    const newStatuses = {};

    allCases.forEach((caseItem) => {
      const caseId = caseItem.id || caseItem.accidentCaseNo;
      const currentStatus = caseItem.currentStatus;
      const previousStatus = prevCaseStatuses[caseId];

      newStatuses[caseId] = currentStatus;

      // Check if status changed (ANY status change triggers notification)
      if (previousStatus && previousStatus !== currentStatus) {
        console.log(`Status change detected for case ${caseItem.accidentCaseNo}: ${previousStatus} → ${currentStatus}`);
        newUpdates.push({
          caseId,
          accidentCaseNo: caseItem.accidentCaseNo,
          previousStatus,
          currentStatus,
          vehicleNumber: caseItem.vehicleNumber,
        });
      }
    });

    // Update previous statuses
    setPrevCaseStatuses(newStatuses);

    // Set notification flag and updated cases list
    if (newUpdates.length > 0) {
      console.log(`New updates detected:`, newUpdates);
      setUpdatedCases(newUpdates);
      setHasUpdate(true);
    }
  }, [allCases]);

  // Auto-refetch cases every 5 seconds to detect status changes
  useEffect(() => {
    console.log('Setting up auto-refetch interval');
    const interval = setInterval(() => {
      console.log('Auto-refetching cases...');
      refetchCases();
    }, 5000);

    return () => {
      console.log('Clearing auto-refetch interval');
      clearInterval(interval);
    };
  }, [refetchCases]);

  const DriverName = ({ caseId }) => {
  const [name, setName] = useState("");

  useEffect(() => {
    const fetchDriver = async () => {
      try {
        const res = await getCaseById(caseId);
        setName(res?.details?.driverName || "");
      } catch {}
    };
    fetchDriver();
  }, [caseId]);

  return name || <span style={{ opacity: 0.35 }}>—</span>;
};

  // ── useSearchCase ─────────────────────────────────────────────
  // Fires only when user clicks Search → we call refetch().
  const {
    isFetching: searching,
    isError:    searchError,
    error:      searchErrorMsg,
    refetch:    runSearch,
  } = useSearchCase(query);

  const {
    isFetching: globalQFetching,
    isError:    globalQError,
    error:      globalQErrorMsg,
    refetch:    runGlobalSearch,
  } = useGlobalCaseSearch(query);

  const tdBorder = isDark
    ? "1px solid rgba(255,255,255,0.05)"
    : "1px solid rgba(99,102,241,0.07)";

  // Filter table live as user types — searches accidentCaseNo
  const filtered = query.trim()
    ? (globalSearchResult ? [globalSearchResult] : allCases.filter((c) =>
        (c.accidentCaseNo || c.id)
          ?.toString()
          .toLowerCase()
          .includes(query.trim().toLowerCase())
      ))
    : allCases;

  const handleSearch = async () => {
    if (!query.trim()) return;
    setGlobalSearchError(null);
    setGlobalSearchResult(null);
    setGlobalSearching(true);

    try {
      const caseNo = query.trim().toUpperCase();
      const r = await runGlobalSearch();

      // r.data is the value returned by getCaseByAccidentNo -> { case, details, documents, timeline }
      if (r?.data?.case) {
        setGlobalSearchResult(r.data.case);
      } else if (r?.error) {
        setGlobalSearchError(r.error.message || `No case with ID "${query}" exists.`);
      } else {
        setGlobalSearchError(r?.data?.message || `No case with ID "${query}" exists.`);
      }
    } catch (err) {
      setGlobalSearchError(err.message || 'Search failed');
      setGlobalSearchResult(null);
    } finally {
      setGlobalSearching(false);
    }
  };

  const handleClear = () => {
    setQuery("");
    setGlobalSearchResult(null);
    setGlobalSearchError(null);
  };

  const getIdx = (c) => allCases.findIndex((x) => x.id === c.id);

  return (
    <div className="cw-page" style={{ minHeight: "100vh", overflowY: "auto" }}>
      <Bg />
      <div className="cw-fixed-controls">
        <div style={{ position: "relative" }}>
          <span style={{ fontSize: 20, cursor: "pointer" }} onClick={() => setShowModal(true)}>🔔</span>
          {hasUpdate && (
            <>
              <span style={{ position: "absolute", top: -2, right: -2, width: 8, height: 8, background: "red", borderRadius: "50%" }}></span>
              {updatedCases.length > 1 && (
                <span style={{
                  position: "absolute",
                  top: -6,
                  right: -6,
                  background: "#ef4444",
                  color: "white",
                  borderRadius: "8px",
                  padding: "1px 4px",
                  fontSize: 9,
                  fontWeight: "bold",
                  minWidth: "14px",
                  textAlign: "center",
                  border: "1px solid white"
                }}>
                  {updatedCases.length}
                </span>
              )}
            </>
          )}
        </div>
        <Toggle isDark={isDark} onToggle={onToggle} />
      </div>

      <div
        style={{
          position: "relative",
          zIndex: 1,
          maxWidth: 860,
          margin: "0 auto",
          padding: "60px 16px 60px",
          display: "flex",
          flexDirection: "column",
          gap: 20,
        }}
      >
        {/* ── Brand + search card ── */}
        <div
          className="cw-cardIn"
          style={{
            position: "relative",
            borderRadius: 20,
            padding: "32px 28px",
            background: isDark ? "rgba(8,12,28,0.96)" : "#ffffff",
            border: isDark
              ? "1px solid rgba(125,211,252,0.15)"
              : "1.5px solid rgba(99,102,241,0.14)",
            boxShadow: isDark
              ? "0 16px 48px rgba(0,0,0,0.6)"
              : "0 16px 48px rgba(99,102,241,0.14)",
          }}
        >
          <div className="cw-entry-bar" />

          {/* Brand */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 20,
            }}
          >
            <div className="cw-float" style={{ fontSize: 36, lineHeight: 1 }}>
              🚛
            </div>
            <div>
              <h1
                className="cw-syne"
                style={{
                  fontSize: 22,
                  fontWeight: 800,
                  letterSpacing: "-0.5px",
                  marginBottom: 2,
                  color: isDark ? "#818cf8" : "#4f46e5",
                }}
              >
                Vehicle Track
              </h1>
              <p className="cw-muted" style={{ fontSize: 11 }}>
                Workshop Repair Status Portal
              </p>
            </div>
          </div>

          {/* Search row */}
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder="Search Case ID"
              className="cw-mono"
              style={{
                flex: 1,
                minWidth: 160,
                padding: "12px 16px",
                borderRadius: 10,
                fontSize: 13,
                letterSpacing: 0.8,
                color: isDark ? "#e2e8f0" : "#1e1b4b",
                background: isDark
                  ? "rgba(255,255,255,0.06)"
                  : "rgba(99,102,241,0.04)",
                border: (globalSearchError || searchError)
                  ? "1.5px solid rgba(239,68,68,0.5)"
                  : isDark
                    ? "1px solid rgba(125,211,252,0.2)"
                    : "1.5px solid rgba(99,102,241,0.2)",
                boxShadow: isDark ? "none" : "0 2px 12px rgba(99,102,241,0.08)",
                outline: "none",
              }}
            />
            <Btn
              onClick={handleSearch}
              disabled={searching || globalSearching || !query.trim()}
              sx={{
                padding: "12px 20px",
                fontSize: 13,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {(searching || globalSearching) ? "Searching…" : "🔍 Search"}
            </Btn>
            {query && (
              <button
                onClick={handleClear}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  flexShrink: 0,
                  color: isDark
                    ? "rgba(255,255,255,0.4)"
                    : "rgba(30,27,75,0.4)",
                  fontSize: 12,
                  padding: "4px 6px",
                }}
              >
                ✕
              </button>
            )}
          </div>

          {/* Search error — case not found */}
          {(globalSearchError || searchError) && (
            <div
              style={{
                marginTop: 14,
                padding: "12px 16px",
                borderRadius: 10,
                background: "rgba(239,68,68,0.08)",
                border: "1.5px solid rgba(239,68,68,0.25)",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <span style={{ fontSize: 18 }}>🔎</span>
              <div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#dc2626",
                    marginBottom: 2,
                  }}
                >
                  Case not found
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: isDark
                      ? "rgba(255,255,255,0.5)"
                      : "rgba(30,27,75,0.5)",
                  }}
                >
                  {globalSearchError || searchErrorMsg?.message ||
                    `No case with ID "${query}" exists.`}{" "}
                  Click <strong>+ Open Form</strong> to create it.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Cases table — always visible ── */}
        <div
          className="cw-fadeUp"
          style={{
            borderRadius: 20,
            overflow: "hidden",
            background: isDark ? "rgba(8,12,28,0.96)" : "#ffffff",
            border: isDark
              ? "1px solid rgba(125,211,252,0.12)"
              : "1.5px solid rgba(99,102,241,0.1)",
            boxShadow: isDark
              ? "0 8px 40px rgba(0,0,0,0.5)"
              : "0 8px 40px rgba(99,102,241,0.1)",
          }}
        >
          <div
            style={{
              padding: "16px 22px",
              borderBottom: isDark
                ? "1px solid rgba(125,211,252,0.08)"
                : "1.5px solid rgba(99,102,241,0.08)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <h2
                className="cw-syne"
                style={{
                  fontSize: 15,
                  fontWeight: 800,
                  color: isDark ? "#f1f5f9" : "#1e1b4b",
                  marginBottom: 2,
                }}
              >
                Registered Cases
              </h2>
              <p className="cw-muted" style={{ fontSize: 11 }}>
                {casesLoading
                  ? "Loading…"
                  : allCases.length === 0
                    ? "No cases registered yet"
                    : query.trim()
                      ? `${filtered.length} of ${allCases.length} match`
                      : `${allCases.length} case${allCases.length !== 1 ? "s" : ""} on record`}
              </p>
            </div>
            {/* Refresh button */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
              <button
                onClick={() => refetchCases()}
                disabled={casesLoading}
                style={{
                  background: "none",
                  border: "none",
                  cursor: casesLoading ? "not-allowed" : "pointer",
                  fontSize: 16,
                  color: isDark ? "rgba(255,255,255,0.3)" : "rgba(30,27,75,0.3)",
                  padding: 4,
                  lineHeight: 1,
                  opacity: casesLoading ? 0.5 : 1,
                }}
                title="Refresh cases"
              >
                {casesLoading ? "⟳" : "↻"}
              </button>
              <span style={{
                fontSize: 9,
                color: isDark ? "rgba(255,255,255,0.4)" : "rgba(30,27,75,0.4)",
                fontFamily: "monospace",
                whiteSpace: "nowrap"
              }}>
                Auto: 5s
              </span>
            </div>
          </div>

          {/* Error loading cases */}
          {casesError && (
            <div style={{ padding: "20px 22px" }}>
              <Alert
                msg={casesErrorMsg?.message || "Failed to load cases."}
                type="error"
              />
            </div>
          )}

          {/* Loading skeleton */}
          {casesLoading && (
            <div style={{ padding: "32px 22px", textAlign: "center" }}>
              <div className="cw-muted" style={{ fontSize: 13 }}>
                Loading cases…
              </div>
            </div>
          )}

          {/* Empty state */}
          {!casesLoading && allCases.length === 0 && !casesError && (
            <div style={{ textAlign: "center", padding: "48px 20px" }}>
              <div style={{ fontSize: 38, marginBottom: 10 }}>📭</div>
              <div className="cw-muted" style={{ fontSize: 13 }}>
                No cases yet. Enter a Case ID above.
              </div>
            </div>
          )}

          {/* Search has no match */}
          {!casesLoading &&
            allCases.length > 0 &&
            query.trim() &&
            filtered.length === 0 && (
              <div style={{ textAlign: "center", padding: "36px 20px" }}>
                <div style={{ fontSize: 30, marginBottom: 8 }}>🔍</div>
                <div className="cw-muted" style={{ fontSize: 13 }}>
                  No cases match <span className="cw-mono">"{query}"</span>
                </div>
              </div>
            )}

          {/* Desktop table */}
          {!casesLoading && filtered.length > 0 && (
            <div className="cw-tbl-wrap">
              <table className="cw-table">
              <thead style={{
  background: isDark ? "rgba(30,27,75,0.95)" : "rgba(235,237,255,0.95)",
  borderBottom: isDark ? "1px solid rgba(99,102,241,0.3)" : "1.5px solid rgba(99,102,241,0.14)",
}}>
  <tr>
    {["Case ID", "Vehicle", "Driver", "Date", "Status", "Actions"].map((h) => (
      <th key={h} style={{
        color: isDark ? "#f1f5f9" : "#ffffff",
      }}>{h}</th>
    ))}
  </tr>
</thead>
                <tbody>
                  {(() => {
                    const displayed = filtered.slice().reverse();
                    return displayed.map((c, i) => {
                      return (
                        <tr
                          key={c.id}
                          onClick={() => onView(i, displayed)}
                          style={{ cursor: "pointer" }}
                        >
                          <td style={{ borderBottom: tdBorder }}>
                            <Badge id={c.accidentCaseNo || c.id} />
                          </td>
                          <td
                            style={{
                              borderBottom: tdBorder,
                              fontWeight: 600,
                              color: isDark ? "#f1f5f9" : "#1e1b4b",
                            }}
                          >
                            {c.vehicleNumber || (
                              <span style={{ opacity: 0.35 }}>—</span>
                            )}
                          </td>
                          <td
                            style={{
                              borderBottom: tdBorder,
                              color: isDark
                                ? "rgba(255,255,255,0.7)"
                                : "rgba(30,27,75,0.65)",
                            }}
                          >
                            <DriverName caseId={c.accidentCaseNo || c.id} />
                          </td>
                          <td
                            style={{
                              borderBottom: tdBorder,
                              fontFamily: "'DM Mono',monospace",
                              fontSize: 12,
                              color: isDark
                                ? "rgba(255,255,255,0.5)"
                                : "rgba(30,27,75,0.45)",
                            }}
                          >
                            {c.createdAt
                            ? (() => {
                                const [y, m, d] = c.createdAt.slice(0, 10).split("-");
                                return `${d}/${m}/${y}`;
                              })()
                            : <span style={{ opacity: 0.35 }}>—</span>
                          }
                          </td>
                          <td style={{ borderBottom: tdBorder }}>
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                padding: "4px 12px",
                                borderRadius: 14,
                                background: "rgba(245,158,11,0.1)",
                                color: "#d97706",
                                border: "1.5px solid rgba(245,158,11,0.2)",
                              }}
                            >
                              {c.currentStatus || c.stage || "In Progress"}
                            </span>
                          </td>
                          <td
                            style={{ borderBottom: tdBorder }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="cw-actions">
                              <Btn
                                cls="cw-btn-violet"
                                onClick={() => onView(i, displayed)}
                              >
                                👁 View
                              </Btn>
                              <Btn
                                cls="cw-btn-amber"
                                onClick={() => onEdit(i, displayed)}
                              >
                                ✏️ Edit
                              </Btn>
                            </div>
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          )}

          {/* Mobile cards */}
          <div
            className="cw-cards-wrap"
            style={{
              display: "none",
              flexDirection: "column",
              gap: 10,
              padding: 14,
            }}
          >
            {(() => {
              const displayed = filtered.slice().reverse();
              return displayed.map((c, i) => (
                <div
                  key={c.id}
                  className="cw-mobile-card"
                  onClick={() => onView(i, displayed)}
                  style={{ cursor: "pointer" }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 8,
                    }}
                  >
                    <Badge id={c.accidentCaseNo || c.id} />
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        padding: "4px 10px",
                        borderRadius: 12,
                        background: "rgba(245,158,11,0.1)",
                        color: "#d97706",
                        border: "1.5px solid rgba(245,158,11,0.2)",
                      }}
                    >
                      🔄 In Progress
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: isDark ? "#f1f5f9" : "#1e1b4b",
                      marginBottom: 3,
                    }}
                  >
                    {c.vehicleNumber || "—"}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: isDark
                        ? "rgba(255,255,255,0.55)"
                        : "rgba(30,27,75,0.55)",
                      marginBottom: 14,
                    }}
                  >
                    {c.driverName || c.details?.driverName || "—"}
                    {c.createdAt
                    ? ` · ${(() => { const [y, m, d] = c.createdAt.slice(0, 10).split("-"); return `${d}/${m}/${y}`; })()}`: ""}
                  </div>
                  <div
                    style={{ display: "flex", gap: 10 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Btn
                      cls="cw-btn-violet"
                      onClick={() => onView(i, displayed)}
                      sx={{ flex: 1, padding: "8px 0" }}
                    >
                      👁 View
                    </Btn>
                    <Btn
                      cls="cw-btn-amber"
                      onClick={() => onEdit(i, displayed)}
                      sx={{ flex: 1, padding: "8px 0" }}
                    >
                      ✏️ Edit
                    </Btn>
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
      </div>

      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.5)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              background: isDark ? "rgba(8,12,28,0.96)" : "#ffffff",
              border: isDark
                ? "1px solid rgba(125,211,252,0.15)"
                : "1.5px solid rgba(99,102,241,0.14)",
              borderRadius: 20,
              padding: "32px 28px",
              maxWidth: 400,
              maxHeight: "70vh",
              overflowY: "auto",
              textAlign: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: 24, marginBottom: 16 }}>🔔</div>
            <h3
              className="cw-syne"
              style={{
                fontSize: 18,
                fontWeight: 800,
                marginBottom: 12,
                color: isDark ? "#f1f5f9" : "#1e1b4b",
              }}
            >
              {updatedCases.length > 0 ? "Case Updates" : "No Updates"}
            </h3>
            <p
              className="cw-muted"
              style={{ fontSize: 14, marginBottom: 20 }}
            >
              {updatedCases.length > 0
                ? `${updatedCases.length} case${updatedCases.length !== 1 ? "s" : ""} updated`
                : "There are currently no status updates available for your cases."}
            </p>

            {updatedCases.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  marginBottom: 20,
                  textAlign: "left",
                }}
              >
                {updatedCases.map((update) => (
                  <div
                    key={update.caseId}
                    style={{
                      padding: "12px 14px",
                      borderRadius: 10,
                      background: isDark ? "rgba(99,102,241,0.1)" : "rgba(99,102,241,0.08)",
                      border: isDark
                        ? "1px solid rgba(125,211,252,0.2)"
                        : "1px solid rgba(99,102,241,0.2)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: isDark ? "#a5b4fc" : "#4f46e5",
                        marginBottom: 4,
                      }}
                    >
                      {update.accidentCaseNo}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: isDark ? "rgba(255,255,255,0.6)" : "rgba(30,27,75,0.6)",
                        marginBottom: 4,
                      }}
                    >
                      Vehicle: {update.vehicleNumber}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: isDark ? "rgba(255,255,255,0.5)" : "rgba(30,27,75,0.5)",
                      }}
                    >
                      <span style={{ color: isDark ? "#9ca3af" : "#6b7280" }}>
                        {update.previousStatus}
                      </span>
                      <span style={{ margin: "0 6px" }}>→</span>
                      <span
                        style={{
                          fontWeight: 600,
                          color: isDark ? "#10b981" : "#059669",
                        }}
                      >
                        {update.currentStatus}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <Btn
              onClick={() => {
                setShowModal(false);
                setHasUpdate(false);
                setUpdatedCases([]);
              }}
            >
              OK
            </Btn>
          </div>
        </div>
      )}
    </div>
  );
};
// SCREEN 2 — INSPECTION FORM
// Hooks: useSubmitCase (text fields), useUploadDocuments (files)
// ═══════════════════════════════════════════════════════════════
const InspectionForm = ({ caseId, initial, existingDocuments = [], onSubmitSuccess, onBack, isDark, onToggle }) => {
  const [form,     setForm]     = useState(initial || INITIAL_FORM);

  const [fileRefs, setFileRefs] = useState({});
  const [progress, setProgress] = useState("");
  const [alert,    setAlert]    = useState({ msg: "", type: "" });
  const [toastVisible, setToastVisible] = useState(false);
  const alertRef = useRef(null);

  const [vehicleLookupLoading, setVehicleLookupLoading] = useState(false);

  const I  = getInp(isDark);
  const ch = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  const handleFile = (key) => (e) =>
    setFileRefs((p) => ({ ...p, [key]: e.target.files[0] || null }));

  const fetchVehicleData = async (vehicleNumber) => {
  if (!vehicleNumber || vehicleNumber.length < 8) return;

  setVehicleLookupLoading(true);
  try {
    const response = await axios.get(
      `/api/vehicle-details/${vehicleNumber}`,
        {
          baseURL: process.env.REACT_APP_BACKEND_URL,
        }
    );

    if (response.data?.insuranceCompany || response.data?.insurancePolicyNo) {
      setForm((p) => ({
        ...p,
        insuranceCompany: response.data.insuranceCompany || p.insuranceCompany,
        policyNumber:     response.data.insurancePolicyNo || p.policyNumber,
      }));
    } else {
      setAlert({ msg: "No insurance details found for this vehicle.", type: "info" });
    }

  } catch (error) {
    console.error("Error fetching vehicle details:", error);
    setAlert({ msg: "Could not fetch vehicle details. Please fill manually.", type: "error" });
  } finally {
    setVehicleLookupLoading(false);
  }
};

const handleVehicleNumberChange = (e) => {
  const value = e.target.value.toUpperCase();
  setForm((p) => ({ ...p, vehicleNumber: value }));

  if (value.replace(/[^A-Z0-9]/g, "").length >= 10) {
    fetchVehicleData(value);
  }
};

  const { mutateAsync: submitCase, isPending: submitting } = useSubmitCase();
  const { mutateAsync: uploadDoc } = useUploadDocuments();

  const existingDocByType = useMemo(() => {
    const map = {};
    existingDocuments.forEach((doc) => {
      if (!doc?.documentType) return;
      const key = doc.documentType.toLowerCase();
      if (map[key]) return;
      map[key] = doc;
    });
    return map;
  }, [existingDocuments]);

  const getDocLabel = (label, key) => {
    const doc = existingDocByType[key.toLowerCase()];
    return (
      <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span>{label}</span>
        {doc?.fileUrl && (
          <a
            href={doc.fileUrl}
            target="_blank"
            rel="noreferrer"
            style={{ fontSize: 12, fontWeight: 700, color: isDark ? "#7dd3fc" : "#4f46e5" }}
          >
            View Uploaded
          </a>
        )}
      </span>
    );
  };

  useEffect(() => {
    setForm(initial || INITIAL_FORM);
  }, [initial]);

  useEffect(() => {
    if (alert.type === "error" && alert.msg) {
      setToastVisible(true);
      alertRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      const t = setTimeout(() => setToastVisible(false), 5000);
      return () => clearTimeout(t);
    }
  }, [alert]);

  // Build file upload list from selected files
  const buildFileList = () => {
    const list = [];
    const added = new Set();

    const pushIfPresent = (key) => {
      if (!fileRefs[key] || added.has(key)) return;
      list.push({ documentType: key, file: fileRefs[key] });
      added.add(key);
    };

    DOCUMENT_FIELDS.forEach(({ key }) => pushIfPresent(key));

    DRIVER_DOC_FIELDS.forEach(({ key }) => pushIfPresent(key));

    if (form.helperPresent === "Yes") {
      HELPER_DOC_FIELDS.forEach(({ key }) => pushIfPresent(key));
    }

    if (form.thirdPartyLoss === "Yes") {
      pushIfPresent(FIR_DOC_FIELD.key);
    }

    if (form.isLoaded === "Yes") {
      pushIfPresent(LR_DOC_FIELD.key);
    }

    ACCIDENT_IMAGE_FIELDS.forEach(({ key }) => pushIfPresent(key));

    return list;
  };

  const handleSubmit = async () => {
    if (!form.driverName.trim()) {
      setAlert({ msg: "Driver Name is required.", type: "error" }); return;
    }
    setAlert({ msg: "", type: "" });

    // ── STEP 1: POST /api/customer/cases/:caseId/details ─────
    setProgress("Saving case details…");
    try {
      await submitCase({
        caseId,
        details: {
          driverName:form.driverName,
          vehicleNumber:  form.vehicleNumber  || "",
          driverNumber:form.driverNumber || "",
          driverDlNumber: form.driverDlNumber || "",
          driverDob: form.driverDob || null,      
          helperPresent:form.helperPresent === "Yes",
          helperName:form.helperPresent === "Yes" ? form.helperName   : "",
          helperNumber: form.helperPresent === "Yes" ? form.helperNumber : "",
          helperDlNumber: form.helperDlNumber || "",
          helperDob: form.helperDob || null,
          rootCause:                    form.rootCause                   || "",
          isLoaded:                     form.isLoaded === "Yes",
          thirdPartyLoss:               form.thirdPartyLoss === "Yes",
          firNumber:                    form.thirdPartyLoss === "Yes" ? form.firNumber    : "",
          claimNumber:                  form.claimNumber                 || "",
          insuranceCompany:             form.insuranceCompany            || "",
          policyNumber: form.policyNumber || "",
          companyRepresentativeName:    form.companyRepresentativeName   || "",
          companyRepresentativeEmail:   form.companyRepresentativeEmail  || "",
          companyRepresentativePhone:   form.companyRepresentativePhone  || "",
          isAdvocateNeeded:             form.isAdvocateNeeded === "Yes",
          powerOfAttorneyHolderName:    form.isAdvocateNeeded === "Yes" ? form.powerOfAttorneyHolderName   : "",
          powerOfAttorneyHolderAadhar:  form.isAdvocateNeeded === "Yes" ? form.powerOfAttorneyHolderAadhar : "",
          firOrGdDate:                  form.firOrGdDate                 || null,
          vehicleReleaseDate:           form.vehicleReleaseDate          || null,
          jamantiDate:                  form.jamantiDate                 || null,
        },
      });
    } catch (err) {
      setAlert({ msg: err.message, type: "error" });
      setProgress("");
      return;
    }

    // ── STEP 2: POST /api/customer/cases/:caseId/documents ───
    const fileList   = buildFileList();
    console.log("File List-------",fileList)
    const fileErrors = [];
    if (fileList.length > 0) {
      for (let i = 0; i < fileList.length; i++) {
        const { documentType, file } = fileList[i];
        setProgress(`Uploading documents… ${i + 1}/${fileList.length}`);
        try {
          await uploadDoc({ caseId, documentType, file });
        } catch (err) {
          fileErrors.push(`${documentType}: ${err.message}`);
        }
      }
    }

    setProgress("");

    if (fileErrors.length > 0) {
      setAlert({ msg: `Case saved. Some files failed: ${fileErrors.join(", ")}`, type: "info" });
      return;
    }

    onSubmitSuccess();
  };

  // Third Party ⓘ tooltip label
  const ThirdPartyLabel = () => (
    <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
      Third Party Loss
      <span style={{ position: "relative", display: "inline-flex" }}
        onMouseEnter={e => e.currentTarget.querySelector(".tp-tip").style.display = "block"}
        onMouseLeave={e => e.currentTarget.querySelector(".tp-tip").style.display = "none"}>
        <span style={{
          width: 15, height: 15, borderRadius: "50%", cursor: "pointer",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          fontSize: 9, fontWeight: 800, lineHeight: 1,
          background: isDark ? "rgba(125,211,252,0.15)" : "rgba(99,102,241,0.12)",
          color: isDark ? "#7dd3fc" : "#6366f1",
          border: isDark ? "1px solid rgba(125,211,252,0.3)" : "1px solid rgba(99,102,241,0.25)",
        }}>ⓘ</span>
        <span className="tp-tip" style={{
          display: "none", position: "absolute", bottom: "calc(100% + 6px)", left: "50%",
          transform: "translateX(-50%)", zIndex: 999,
          background: isDark ? "#1e2535" : "#1e1b4b",
          color: "#fff", fontSize: 11, lineHeight: 1.6,
          padding: "8px 12px", borderRadius: 8, whiteSpace: "nowrap",
          boxShadow: "0 8px 24px rgba(0,0,0,0.25)", pointerEvents: "none",
        }}>
          <strong style={{ display: "block", marginBottom: 3, color: "#a5b4fc" }}>Third Party Claim Types</strong>
          • Third Party Death<br />• Third Party Injury<br />• Vehicle Theft<br />• Vehicle Damage
        </span>
      </span>
    </span>
  );

  return (
    <div className="cw-page">
      <Bg />
      <Nav
        left={
          <Btn cls={isDark ? "cw-btn-ghost-d" : "cw-btn-ghost-l"}
            onClick={onBack} sx={{ padding: "7px 14px", fontSize: 13 }}>← Back</Btn>
        }
        center={
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <span className="cw-muted" style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: 2 }}>Case ID</span>
            <Badge id={caseId} />
          </div>
        }
        right={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Toggle isDark={isDark} onToggle={onToggle} />
            <span style={{
              fontSize: 11, fontWeight: 700, padding: "5px 12px", borderRadius: 20,
              background: isDark ? "rgba(251,191,36,0.1)" : "rgba(245,158,11,0.1)",
              color: isDark ? "#fbbf24" : "#d97706",
              border: isDark ? "1px solid rgba(251,191,36,0.2)" : "1.5px solid rgba(245,158,11,0.2)",
            }}>✏️ Inspection Form</span>
          </div>
        }
      />

      <div style={{ position: "relative", zIndex: 1, padding: "24px 20px 60px", maxWidth: 1080, margin: "0 auto" }}>
        <div className="cw-fadeUp" style={{
          borderRadius: 20, padding: "32px 28px",
          background: isDark ? "rgba(8,12,28,0.96)" : "#ffffff",
          border: isDark ? "1px solid rgba(125,211,252,0.1)" : "1.5px solid rgba(99,102,241,0.1)",
          boxShadow: isDark ? "0 12px 48px rgba(0,0,0,0.55)" : "0 12px 48px rgba(99,102,241,0.1)",
          backdropFilter: "blur(20px)",
        }}>
          <h2 className="cw-syne" style={{
            fontSize: 22, fontWeight: 800, marginBottom: 26,
            color: isDark ? "#f1f5f9" : "#1e1b4b",
            display: "flex", alignItems: "center", gap: 10,
          }}>
            <span style={{
              width: 36, height: 36, borderRadius: 10, display: "inline-flex",
              alignItems: "center", justifyContent: "center", fontSize: 18,
              background: isDark ? "rgba(125,211,252,0.1)" : "rgba(99,102,241,0.1)",
            }}>📋</span>
            Inspection Form
          </h2>

          <div ref={alertRef}>
            {alert.msg && <Alert msg={alert.msg} type={alert.type} />}
          </div>
          {progress  && <Alert msg={progress}  type="info" />}

          {/* ── Driver Details ── */}
          
          <Section>Driver Details</Section>
          <div className="cw-form-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 16 }}>

  <Field label="Driver Name *">
    <input name="driverName" value={form.driverName} onChange={ch} style={I} />
  </Field>

  <Field label="Driver Phone Number">
    <input
      name="driverNumber"
      value={form.driverNumber}
      onChange={(e) => {
        const val = e.target.value.replace(/\D/g, "").slice(0, 10);
        setForm((p) => ({ ...p, driverNumber: val }));
      }}
      style={I}
    />
  </Field>

  <Field label="Vehicle Number">
    <input name="vehicleNumber" value={form.vehicleNumber} 
    onChange={handleVehicleNumberChange}
    style={I} />
  </Field>

  <Field label="Driver DL Number">
    <input name="driverDlNumber" value={form.driverDlNumber} onChange={ch} style={I} />
  </Field>

  <Field label="Driver DOB">
    <input type="date" name="driverDob" value={form.driverDob} onChange={ch} style={I} />
  </Field>

  {DRIVER_DOC_FIELDS.map(({ key, label }) => (
    <Field key={key} label={getDocLabel(label, key)}>
      <FileUpload onChange={handleFile(key)} isDark={isDark} />
    </Field>
  ))}

</div>

          {/* ── Vehicle Documents ── */}
          <Section>Vehicle Documents</Section>
          <div className="cw-form-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 16 }}>
            {DOCUMENT_FIELDS.map(({ key, label }) => (
              <Field key={key} label={getDocLabel(label, key)}>
                <FileUpload onChange={handleFile(key)} isDark={isDark} />
              </Field>
            ))}
          </div>

          {/* ── Helper / Co-Pilot ── */}
          <Section>Helper / Co-Pilot</Section>
          <div className="cw-form-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 16 }}>
            <Field label="Helper Present">
              <select name="helperPresent" value={form.helperPresent} onChange={ch} style={I}>
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </Field>
            {form.helperPresent === "Yes" && <>
            <Field label="Co-Pilot DL Number">
  <input name="helperDlNumber" value={form.helperDlNumber} onChange={ch} style={I} />
</Field>

<Field label="Co-Pilot DOB">
  <input type="date" name="helperDob" value={form.helperDob} onChange={ch} style={I} />
</Field>
              <Field label="Co-Pilot Name">
                <input name="helperName" value={form.helperName} onChange={ch} style={I} placeholder=" Enter Co-Pilot Name"/>
              </Field>
              <Field label="Co-Pilot Phone Number">
                 <input
    name="helperNumber"
    value={form.helperNumber}
    onChange={(e) => {
      const val = e.target.value.replace(/\D/g, "").slice(0, 10);
      setForm((p) => ({ ...p, helperNumber: val }));
    }}
    style={{
      ...I,
      border: form.helperNumber && form.helperNumber.length < 10
        ? "1.5px solid rgba(239,68,68,0.5)"
        : I.border,
    }}
    placeholder="10-digit number"
    inputMode="numeric"
    maxLength={10}
  />
              </Field>
              {HELPER_DOC_FIELDS.map(({ key, label }) => (
                <Field key={key} label={getDocLabel(label, key)}>
                  <FileUpload onChange={handleFile(key)} isDark={isDark} />
                </Field>
              ))}
            </>}
          </div>

          {/* ── Accident Info ── */}
          <Section>Accident Information</Section>
          <div className="cw-form-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 16 }}>
            <Field label="Accident Date & Time">
              <input type="datetime-local" name="accidentDate" value={form.accidentDate} onChange={ch} style={I} />
            </Field>
            <Field label="Vehicle Loaded">
              <select name="isLoaded" value={form.isLoaded} onChange={ch} style={I}>
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </Field>
            {form.isLoaded === "Yes" && (
              <Field label={getDocLabel(LR_DOC_FIELD.label, LR_DOC_FIELD.key)}>
                <FileUpload onChange={handleFile(LR_DOC_FIELD.key)} isDark={isDark} />
              </Field>
            )}
            <Field label={<ThirdPartyLabel />}>
              <select name="thirdPartyLoss" value={form.thirdPartyLoss} onChange={ch} style={I}>
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </Field>
            {form.thirdPartyLoss === "Yes" && <>
              <Field label="FIR / GD Number">
                <input name="firNumber" value={form.firNumber} onChange={ch} style={I} placeholder="FIR or GD number" />
              </Field>
              <Field label="FIR / GD Date">
                <input type="date" name="firOrGdDate" value={form.firOrGdDate} onChange={ch} style={I} />
              </Field>
              <Field label={getDocLabel(FIR_DOC_FIELD.label, FIR_DOC_FIELD.key)}>
                <FileUpload onChange={handleFile(FIR_DOC_FIELD.key)} isDark={isDark} />
              </Field>
            </>}
          </div>

          {/* ── Root Cause ── */}
          <div style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 7 }}>
            <label className="cw-label">Root Cause / How Accident Happened</label>
            <textarea name="rootCause" value={form.rootCause} onChange={ch} rows={4}
              style={{ ...I, resize: "vertical", minHeight: 96 }} />
          </div>

          {/* ── Accident Images ── */}
          <Section>Accident Images</Section>
          <div className="cw-form-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 16 }}>
            {ACCIDENT_IMAGE_FIELDS.map(({ key, label }) => (
              <Field key={key} label={getDocLabel(label, key)}>
                <FileUpload onChange={handleFile(LR_DOC_FIELD.key)} isDark={isDark} />
              </Field>
            ))}
          </div>

          {/* ── Claim Details ── */}
          <Section>Claim Details</Section>
         <div className="cw-form-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 16 }}>

  <Field label="Claim Number">
    <input name="claimNumber" value={form.claimNumber} onChange={ch} style={I} />
  </Field>

  <Field label="Insurance Company">
    <input name="insuranceCompany" value={form.insuranceCompany} onChange={ch} style={I} />
  </Field>

  <Field label="Policy Number">
    <input name="policyNumber" value={form.policyNumber} onChange={ch} style={I} />
  </Field>

  <Field label="Vehicle Release Date">
    <input type="date" name="vehicleReleaseDate" value={form.vehicleReleaseDate} onChange={ch} style={I} />
  </Field>

  <Field label="Jamanti Date">
    <input type="date" name="jamantiDate" value={form.jamantiDate} onChange={ch} style={I} />
  </Field>

</div>

          {/* ── Advocate / Power of Attorney ── */}
          <Section>Advocate / Power of Attorney</Section>
          <div className="cw-form-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 16 }}>
            <Field label="Advocate Needed">
              <select name="isAdvocateNeeded" value={form.isAdvocateNeeded} onChange={ch} style={I}>
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </Field>
            {form.isAdvocateNeeded === "Yes" && <>
              <Field label="POA Holder Name">
                <input name="powerOfAttorneyHolderName" value={form.powerOfAttorneyHolderName} onChange={ch} style={I} />
              </Field>
              <Field label="POA Holder Aadhar">
                <input name="powerOfAttorneyHolderAadhar" value={form.powerOfAttorneyHolderAadhar} onChange={ch} style={I} placeholder="12-digit Aadhar" />
              </Field>
            </>}
          </div>

          {/* ── Company Representative ── */}
          <Section>Company Representative</Section>
          <div className="cw-form-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 16 }}>
            <Field label="Representative Name">
              <input name="companyRepresentativeName" value={form.companyRepresentativeName} onChange={ch} style={I} placeholder=" Enter Representative Name" />
            </Field>
            <Field label="Email">
              <input type="email" name="companyRepresentativeEmail" value={form.companyRepresentativeEmail} onChange={ch} style={I} placeholder=" Enter Representative Email"/>
            </Field>
            <Field label="Phone Number">
              <input
    name="companyRepresentativePhone"
    value={form.companyRepresentativePhone}
    onChange={(e) => {
      const val = e.target.value.replace(/\D/g, "").slice(0, 10);
      setForm((p) => ({ ...p, companyRepresentativePhone: val }));
    }}
    style={{
      ...I,
      border: form.companyRepresentativePhone && form.companyRepresentativePhone.length < 10
        ? "1.5px solid rgba(239,68,68,0.5)"
        : I.border,
    }}
    placeholder="10-digit number"
    inputMode="numeric"
    maxLength={10}
  />

            </Field>
          </div>

          <div style={{ marginTop: 28, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Btn onClick={handleSubmit} disabled={submitting}
              sx={{ padding: "12px 63px", fontSize: 15 }}>
              {submitting ? "Submitting…" : "✓ Submit Case"}
            </Btn>
            <Btn cls="cw-btn-danger" onClick={onBack} disabled={submitting}
              sx={{ padding: "12px 22px", fontSize: 14 }}>Cancel</Btn>
          </div>
        </div>
      </div>

      {/* ── Fixed error toast ── */}
      {toastVisible && alert.type === "error" && (
        <div style={{
          position: "fixed",
          bottom: 28,
          right: 24,
          zIndex: 9999,
          maxWidth: 400,
          minWidth: 280,
          padding: "14px 16px",
          borderRadius: 14,
          background: "#dc2626",
          color: "#fff",
          fontSize: 13,
          fontWeight: 600,
          lineHeight: 1.55,
          boxShadow: "0 12px 40px rgba(220,38,38,0.4), 0 4px 16px rgba(0,0,0,0.2)",
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          border: "1.5px solid rgba(255,255,255,0.2)",
        }}>
          <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>⚠️</span>
          <span style={{ flex: 1 }}>{alert.msg}</span>
          <button
            onClick={() => setToastVisible(false)}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              color: "#fff",
              cursor: "pointer",
              fontSize: 11,
              fontWeight: 700,
              padding: "3px 8px",
              borderRadius: 6,
              flexShrink: 0,
              marginTop: 1,
            }}
          >✕</button>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// ROOT
// ═══════════════════════════════════════════════════════════════
const WorkshopForm = ({ onViewCase }) => {
  const { caseId: urlCaseId } = useParams(); 
   const navigate = useNavigate();

  const [screen,   setScreen]   = useState(() => {
    if (urlCaseId) return "form";
    return localStorage.getItem("cw-screen") || "entry"});

  const [caseId,   setCaseId]   = useState(() => urlCaseId ||localStorage.getItem("cw-caseId") || "");
  const [editData, setEditData] = useState(() => { try { return JSON.parse(localStorage.getItem("cw-editData")) || null; } catch { return null; }});  // prefills form on edit
  const [editDocuments, setEditDocuments] = useState(() => { try { return JSON.parse(localStorage.getItem("cw-editDocs")) || []; } catch { return []; }});
  const [viewData, setViewData] = useState(() => { try { return JSON.parse(localStorage.getItem("cw-viewData")) || null; } catch { return null; }});  // passed to tracking view
  const [isDark,   setIsDark]   = useState(getIsDark);

  useEffect(() => { applyTheme(isDark); }, [isDark]);

   useEffect(() => {
    localStorage.setItem("cw-screen",   screen);
    localStorage.setItem("cw-caseId",   caseId);
    localStorage.setItem("cw-editData", JSON.stringify(editData));
    localStorage.setItem("cw-editDocs", JSON.stringify(editDocuments));
    localStorage.setItem("cw-viewData", JSON.stringify(viewData));
  }, [screen, caseId, editData, editDocuments, viewData]);

  // useAllCases is called inside CaseEntry — ROOT just manages screen state
  const toggle  = () => setIsDark((p) => !p);
  const goEntry = ()    => { setCaseId(""); setEditData(null); setEditDocuments([]); setScreen("entry"); navigate("/customer/workshop-dashboard/"); 
    localStorage.removeItem("cw-caseId");
    localStorage.removeItem("cw-editData");
    localStorage.removeItem("cw-editDocs");
    localStorage.removeItem("cw-viewData");
    localStorage.setItem("cw-screen", "entry");
  };
  const goForm  = (id)  => { setCaseId(id); setEditData(null); setEditDocuments([]); setScreen("form"); };

  // Called after form submits successfully
  const handleFormSuccess = () => {
    localStorage.removeItem("cw-caseId");
    localStorage.removeItem("cw-editData");
    localStorage.removeItem("cw-editDocs");
    localStorage.removeItem("cw-viewData");
    localStorage.setItem("cw-screen", "entry");
    setScreen("entry")
  };
  // Table auto-refreshes because useSubmitCase invalidates the query

  const handleEdit = async (i, cases) => {
    const selected = cases[i];
    const selectedCaseId = selected?.accidentCaseNo || selected?.id;
    if (!selectedCaseId) return;

    navigate(`/customer/workshop-dashboard/${selectedCaseId}`);

    setCaseId(selectedCaseId);
    setEditData(INITIAL_FORM);
    setEditDocuments([]);

    try {
      const response = await getCaseById(selectedCaseId);
      console.log("raw response:", response);
      console.log("response.details:", response?.details);
      console.log("driverDlNumber in details:", response?.details?.driverDlNumber);
    console.log("mapped form:", mapDetailsToForm(response?.details || {}));
      setEditData(mapDetailsToForm(response?.details || {}));
      setEditDocuments(Array.isArray(response?.documents) ? response.documents : []);
    } catch {
      setEditData(INITIAL_FORM);
      setEditDocuments([]);
    }

    setScreen("form");
  };

const viewCase = async (cases, i) => {
  const selected = cases[i];
  const caseId = selected?.accidentCaseNo || selected?.id;

  try {
    const response = await getCaseById(caseId);

    const fullData = {
      ...selected,
      ...(response?.details || {}),
      documents: response?.documents || [],
    };

    setViewData(fullData);

    if (onViewCase) onViewCase(fullData);

  } catch (err) {
    console.error(err);
    setViewData(selected);
  }

  setScreen("view");
};

  const sh = { isDark, onToggle: toggle };

  return (
    <>
      {screen === "entry" && (
        <CaseEntry
          onContinue={goForm}
         onView={async (i, cases) => {
  const selected = cases[i];
  const caseId = selected?.accidentCaseNo || selected?.id;
  navigate(`/customer/workshop-dashboard/${caseId}`);

  try {
    const response = await getCaseById(caseId);

    setViewData({
      ...selected,                 // base data
      ...(response?.details || {}), // full form data
      documents: response?.documents || [],
    });

  } catch (err) {
    console.error(err);
    setViewData(selected);
  }

  setScreen("view");
}}
          onEdit={handleEdit}
          {...sh}
        />
      )}
      {screen === "form" && (
        <InspectionForm
          caseId={caseId}
          initial={editData}
          existingDocuments={editDocuments}
          onSubmitSuccess={handleFormSuccess}
          onBack={goEntry}
          {...sh}
        />
      )}
      {screen === "view" && viewData && (
        <CustomerWorkshopView
          caseData={viewData}
          onBack={goEntry}
        />
      )}
    </>
  );
};

export default WorkshopForm;